<template>
  <div class="single-choice-editor-wrap">
    <main class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questions" class="breadcrumb-link">Questions</router-link>
        / {{ breadcrumbTail }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-subtitle">Single choice: user picks one option from a list</p>
        </div>
        <div class="page-header-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || saving"
            @click="onSave"
          >{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create question') }}</button>
        </div>
      </div>

      <p
        v-if="serverError"
        class="server-error"
        role="alert"
      >{{ serverError }}</p>

      <div
        v-if="isEdit && answerCount > 0"
        class="answer-warning"
        role="status"
      >
        <span class="warning-icon" aria-hidden="true">⚠️</span>
        This question has {{ answerCount }}
        {{ answerCount === 1 ? 'answer' : 'answers' }}.
        Editing labels, options, or scores may affect analysis.
      </div>

      <div v-if="loadError" class="load-error">
        <p>{{ loadError }}</p>
        <router-link to="/admin/questions" class="back-link">← Back</router-link>
      </div>

      <div v-else-if="loading" class="loading-state">
        Loading question…
      </div>

      <div v-else class="editor-grid">
        <!-- ───────── LEFT: form ───────── -->
        <section class="form-column">

          <!-- Fieldset 1: Identification — same shape as the stepper editor -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Identification</legend>

            <div class="field">
              <label class="field-label" :for="`${uid}-code`">Code</label>
              <input
                :id="`${uid}-code`"
                v-model="form.code"
                type="text"
                class="field-input mono"
                :class="{ 'has-error': errors.code && touched.code }"
                placeholder="TOMATOES_FREQ"
                autocomplete="off"
                spellcheck="false"
                @blur="touched.code = true"
                @input="onCodeInput"
              >
              <p class="field-hint">
                Short identifier, uppercase letters, digits, and underscores.
                Must be unique within your organization.
              </p>
              <p
                v-if="errors.code && touched.code"
                class="field-error"
              >{{ errors.code }}</p>
              <p
                v-if="isEdit && form.code !== initialSnapshot.code"
                class="field-hint dim"
              >Changing the code may affect analytics scripts that reference it.</p>
            </div>

            <div class="field">
              <label class="field-label">Section</label>
              <button
                type="button"
                class="section-pick-btn"
                :class="{ 'is-unassigned': form.section_id == null }"
                @click="showSectionPicker = true"
              >
                <span class="section-pick-label">{{ sectionPickLabel }}</span>
                <span class="section-pick-action">{{ form.section_id == null ? 'Pick section' : 'Change' }}</span>
              </button>
              <button
                v-if="form.section_id != null"
                type="button"
                class="section-clear-btn"
                @click="form.section_id = null"
              >Clear (Unassigned)</button>
            </div>

            <div class="field">
              <label class="field-label">Status</label>
              <div class="radio-row">
                <label class="radio-option">
                  <input
                    type="radio"
                    v-model="form.status"
                    value="active"
                  >
                  <span>Active</span>
                </label>
                <label class="radio-option">
                  <input
                    type="radio"
                    v-model="form.status"
                    value="archived"
                  >
                  <span>Archived</span>
                </label>
              </div>
            </div>
          </fieldset>

          <!-- Fieldset 2: Question content — translatable fields under tabs -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Question content</legend>

            <LocaleSelector
              v-model="activeLang"
              :locales="SUPPORTED_LOCALES"
              :labels="LOCALE_LABELS"
              :filled-fn="isLocaleFilled"
              hint="Fill in any or all languages"
              aria-label="Translation language"
            />

            <div class="field">
              <label class="field-label" :for="`${uid}-title-${activeLang}`">
                Question title <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-title-${activeLang}`"
                v-model="form.content.title[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.title && touched.title }"
                rows="2"
                :placeholder="placeholders.title[activeLang]"
                @blur="touched.title = true"
              ></textarea>
              <p class="field-hint">
                The main question text. Required in at least one language.
              </p>
              <p
                v-if="errors.title && touched.title"
                class="field-error"
              >{{ errors.title }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-subtitle-${activeLang}`">
                Subtitle <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-subtitle-${activeLang}`"
                v-model="form.content.subtitle[activeLang]"
                type="text"
                class="field-input"
                :placeholder="placeholders.subtitle[activeLang]"
              >
              <p class="field-hint">
                Shown above the title as a small section label
                (e.g. "Tomatoes / tomato sauce"). Useful when grouping
                related questions visually.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-prompt-${activeLang}`">
                Helper prompt <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-prompt-${activeLang}`"
                v-model="form.content.prompt[activeLang]"
                type="text"
                class="field-input"
                :placeholder="placeholders.prompt[activeLang]"
              >
              <p class="field-hint">
                Shown below the title. Useful for short instructions.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-tooltip-label-${activeLang}`">
                Info button label <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-tooltip-label-${activeLang}`"
                v-model="form.content.tooltip_label[activeLang]"
                type="text"
                class="field-input"
                :placeholder="placeholders.tooltip_label[activeLang]"
              >
              <p class="field-hint">
                Text of the info button shown above the answers. Leave
                empty to hide the button.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-tooltip-body-${activeLang}`">
                Info button body <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-tooltip-body-${activeLang}`"
                v-model="form.content.tooltip_body[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.tooltip_pairing && errors.tooltip_pairing[activeLang] }"
                rows="3"
              ></textarea>
              <p class="field-hint">
                Content of the info popover. Required if the label is
                filled, must be empty if the label is empty.
              </p>
              <p
                v-if="errors.tooltip_pairing && errors.tooltip_pairing[activeLang]"
                class="field-error"
              >{{ errors.tooltip_pairing[activeLang] }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-help-${activeLang}`">
                Help text <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <textarea
                :id="`${uid}-help-${activeLang}`"
                v-model="form.help_text[activeLang]"
                class="field-input field-textarea"
                rows="3"
              ></textarea>
              <p class="field-hint">
                Optional explanation shown via a separate help icon. Leave
                empty in all languages to hide the help icon.
              </p>
            </div>
          </fieldset>

          <!-- Fieldset 3: Answer options — the new fieldset for single_choice -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">
              Answer options and points
              <span class="option-count">{{ form.content.options.length }} of 8 used</span>
            </legend>

            <p
              v-if="errors.options_count"
              class="field-error fieldset-error"
            >{{ errors.options_count }}</p>

            <VueDraggable
              v-model="form.content.options"
              :animation="180"
              handle=".drag-handle"
              item-key="id"
              class="options-list"
            >
              <div
                v-for="(opt, idx) in form.content.options"
                :key="opt.id"
                class="option-row"
              >
                <span
                  class="drag-handle"
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                >⋮⋮</span>

                <span class="option-number">{{ idx + 1 }}</span>

                <div class="option-label-cell">
                  <input
                    type="text"
                    class="field-input option-label-input"
                    :class="{ 'has-error': errors.option_labels[opt.id] }"
                    :placeholder="`Label (${LOCALE_LABELS[activeLang]})`"
                    :value="opt.label[activeLang]"
                    @input="setOptionLabel(opt, activeLang, $event.target.value)"
                  >
                  <p
                    v-if="errors.option_labels[opt.id]"
                    class="field-error option-label-error"
                  >{{ errors.option_labels[opt.id] }}</p>
                </div>

                <div class="option-score-cell">
                  <input
                    type="number"
                    step="1"
                    class="field-input number-input option-score-input"
                    :class="{ 'has-error': errors.option_scores[opt.id] }"
                    v-model.number="opt.score"
                    :aria-label="`Score for option ${idx + 1}`"
                  >
                </div>

                <button
                  type="button"
                  class="option-delete"
                  :disabled="form.content.options.length <= 2"
                  :title="form.content.options.length <= 2
                    ? 'Minimum 2 options required'
                    : 'Delete option'"
                  aria-label="Delete option"
                  @click="deleteOption(idx)"
                >×</button>
              </div>
            </VueDraggable>

            <p class="field-hint score-hint">Score is the same across all languages.</p>

            <button
              type="button"
              class="btn btn-secondary add-option-btn"
              :disabled="form.content.options.length >= 8"
              @click="addOption"
            >+ Add option</button>
          </fieldset>

        </section>

        <!-- ───────── RIGHT: live preview ───────── -->
        <aside class="preview-column">
          <div class="preview-label">Live preview</div>
          <div class="preview-frame">
            <SingleChoiceRenderer
              :title="form.content.title"
              :prompt="form.content.prompt"
              :tooltip-label="form.content.tooltip_label"
              :tooltip-body="form.content.tooltip_body"
              :options="form.content.options"
              :locale="activeLang"
              :section-label="previewSectionLabel"
              :question-index="PREVIEW_INDEX"
              :total-questions="PREVIEW_TOTAL"
              :can-go-back="true"
              :is-last="false"
              :primer-label="primerLabel"
              :primer-content="primerContent"
              :preview="true"
            />
          </div>
          <p class="preview-note">
            Mock screen for the
            <strong>{{ LOCALE_LABELS[activeLang] }}</strong>
            translation. Switch tabs in the form to preview another
            language. The progress chrome is illustrative — actual
            position depends on the questionnaire.
          </p>
        </aside>
      </div>

    </main>

    <!-- Discard-changes confirm dialog -->
    <div
      v-if="showLeaveConfirm"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      @click.self="onCancelLeave"
    >
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Discard changes?</h2>
        <p class="modal-text">Your unsaved changes will be lost.</p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancelLeave"
          >Keep editing</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="onConfirmLeave"
          >Discard</button>
        </div>
      </div>
    </div>

    <!-- Section picker popup -->
    <SectionPickerPopup
      v-if="showSectionPicker"
      :current-section-id="form.section_id"
      @select-section="onSectionPicked"
      @close="showSectionPicker = false"
    />

    <!-- Toast -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus';
import SingleChoiceRenderer from '@/components/quiz/SingleChoiceRenderer.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionPickerPopup from '@/components/admin/SectionPickerPopup.vue';
import apiService from '@/services/apiService';
import {
  questionsState,
  blankI18n,
  ensureAllLocales,
  localizeI18n,
  CODE_REGEX,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '../_data/questions.js';

let uidCounter = 0;

// Fake "Frage 3 von 7" context for the editor preview chrome.
const PREVIEW_INDEX = 2;
const PREVIEW_TOTAL = 7;

// Per SPEC_SINGLE_CHOICE_SCHEMA.md: ^opt_[1-9][0-9]*$
const OPTION_ID_RE = /^opt_[1-9][0-9]*$/;

function trimI18n(i18n) {
  // Trim + drop empty entries from an i18n object. serializeForm uses
  // this for dirty-check and payload; empty locales become absent keys.
  const out = {};
  if (i18n && typeof i18n === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      const v = i18n[loc];
      if (typeof v === 'string' && v.trim()) {
        out[loc] = v.trim();
      }
    }
  }
  return out;
}

function hasAnyLocaleFilled(i18n) {
  if (!i18n || typeof i18n !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => {
    const v = i18n[loc];
    return typeof v === 'string' && v.trim().length > 0;
  });
}

// Localized placeholders for the per-language text fields. Empty string
// where we don't have a translation handy — the input just shows nothing
// rather than a misleading English hint on a French tab.
const FIELD_PLACEHOLDERS = {
  title: {
    en: 'How often per week do you usually eat this?',
    de: 'Wie oft pro Woche isst du diese normalerweise?',
    fr: '', it: '',
  },
  subtitle: {
    en: 'Tomatoes / tomato sauce',
    de: 'Tomaten / Tomatensauce',
    fr: '', it: '',
  },
  prompt: { en: '', de: '', fr: '', it: '' },
  tooltip_label: {
    en: 'What counts here?',
    de: 'Was zählt hier?',
    fr: '', it: '',
  },
};

export default {
  name: 'SingleChoiceEditor',
  components: { SingleChoiceRenderer, LocaleSelector, VueDraggable, SectionPickerPopup },
  data() {
    uidCounter += 1;
    return {
      uid: `sce-${uidCounter}`,
      PREVIEW_INDEX,
      PREVIEW_TOTAL,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      placeholders: FIELD_PLACEHOLDERS,
      activeLang: DEFAULT_LOCALE,
      form: this.buildBlankForm(),
      initialSnapshot: null,
      // Tracks the maximum opt_N integer ever assigned to this question.
      // Ids are NEVER reused on add after a delete; new options get
      // opt_<nextOptionInt+1>. Loaded from the question's existing
      // options on edit; defaults to 0 for new (so the seed 2 options
      // get opt_1 and opt_2).
      nextOptionInt: 0,
      touched: {
        code: false,
        title: false,
      },
      serverError: null,
      loadError: null,
      loading: false,
      saving: false,
      answerCount: 0,
      rejectedCodes: [],
      showLeaveConfirm: false,
      showSectionPicker: false,
      pendingNext: null,
      cancelPending: false,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'QuestionEditorEdit';
    },
    questionId() {
      return this.isEdit ? Number(this.$route.params.id) : null;
    },
    sections() {
      return questionsState.sections;
    },
    /**
     * Label shown on the "Pick section" button: the current section's
     * name in the active locale (with fallback), or "Unassigned" when
     * form.section_id is null. The section picker popup handles all
     * other UX; this button just shows the current state.
     */
    sectionPickLabel() {
      if (this.form.section_id == null) return 'Unassigned';
      const s = this.sections.find(x => x.id === this.form.section_id);
      if (!s) return `Section ${this.form.section_id}`;
      return this.sectionOptionLabel(s);
    },
    breadcrumbTail() {
      if (this.isEdit) {
        return this.initialSnapshot
          ? `Edit · ${this.initialSnapshot.code || 'question'}`
          : 'Edit';
      }
      return 'New single choice question';
    },
    pageTitle() {
      return this.isEdit ? 'Edit question' : 'New single choice question';
    },
    errors() {
      const e = {
        // Per-row label errors keyed by option id, so each row's error
        // attaches to that row even after a reorder.
        option_labels: {},
        // Per-locale tooltip-pairing errors keyed by locale.
        tooltip_pairing: {},
        // Per-row score errors keyed by option id. (Currently the
        // number input prevents non-integer entries, but we still
        // surface integer-only validation to be explicit.)
        option_scores: {},
      };

      const code = (this.form.code || '').trim();
      if (!code) {
        e.code = 'Code is required.';
      } else if (!CODE_REGEX.test(code)) {
        e.code = 'Code must be uppercase letters, digits or underscores. Max 32 chars.';
      } else if (this.rejectedCodes.includes(code)) {
        e.code = 'This code is already used by another question.';
      }

      if (!hasAnyLocaleFilled(this.form.content.title)) {
        e.title = 'Question title is required in at least one language.';
      }

      // Tooltip pairing: per locale, label and body must be jointly
      // empty or jointly filled.
      const tl = this.form.content.tooltip_label;
      const tb = this.form.content.tooltip_body;
      for (const loc of SUPPORTED_LOCALES) {
        const hasLabel = !!(tl[loc] && tl[loc].trim());
        const hasBody = !!(tb[loc] && tb[loc].trim());
        if (hasLabel && !hasBody) {
          e.tooltip_pairing[loc] = 'Info button body is required when the label is filled.';
        } else if (hasBody && !hasLabel) {
          e.tooltip_pairing[loc] = 'Info button label is required when the body is filled.';
        }
      }
      if (Object.keys(e.tooltip_pairing).length === 0) {
        delete e.tooltip_pairing;
      }

      const opts = this.form.content.options;
      if (!Array.isArray(opts) || opts.length < 2) {
        e.options_count = 'At least 2 answer options are required.';
      } else if (opts.length > 8) {
        e.options_count = 'No more than 8 answer options are allowed.';
      }

      for (const opt of (opts || [])) {
        if (!hasAnyLocaleFilled(opt.label)) {
          e.option_labels[opt.id] = 'Label is required in at least one language.';
        }
        if (!Number.isInteger(opt.score)) {
          e.option_scores[opt.id] = 'Score must be an integer.';
        }
      }

      return e;
    },
    isValid() {
      const e = this.errors;
      if (e.code || e.title || e.options_count) return false;
      if (e.tooltip_pairing && Object.keys(e.tooltip_pairing).length > 0) return false;
      if (Object.keys(e.option_labels).length > 0) return false;
      if (Object.keys(e.option_scores).length > 0) return false;
      return true;
    },
    isDirty() {
      if (!this.initialSnapshot) return false;
      return JSON.stringify(this.serializeForm()) !== JSON.stringify(this.initialSnapshot);
    },
    previewSectionLabel() {
      // Per SPEC_SINGLE_CHOICE_EDITOR.md: the renderer's section-label
      // slot displays content.subtitle for single_choice (not the
      // questionnaire section name — that's the stepper's convention).
      return (this.form.content.subtitle[this.activeLang] || '').trim();
    },
    primerContent() {
      // Header primer button is driven by help_text (separate from the
      // inline tooltip below the question).
      const ht = this.form.help_text;
      return (ht && ht[this.activeLang]) ? ht[this.activeLang] : '';
    },
    primerLabel() {
      const byLoc = { en: 'Info', de: 'Info', fr: 'Infos', it: 'Info' };
      return byLoc[this.activeLang] || 'Info';
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (this.isEdit) {
          this.loadExisting(id);
        } else {
          this.resetForNew();
        }
      },
    },
  },
  created() {
    if (!this.isEdit) {
      this.resetForNew();
    }
  },
  mounted() {
    if (!questionsState.sections.length) {
      this.fetchSections();
    }
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    buildBlankForm() {
      // Seed with 2 empty options (opt_1, opt_2, score 0) per the spec's
      // "workable skeleton" rule.
      return {
        code: '',
        section_id: null,
        status: 'active',
        content: {
          title: blankI18n(),
          subtitle: blankI18n(),
          prompt: blankI18n(),
          tooltip_label: blankI18n(),
          tooltip_body: blankI18n(),
          options: [
            { id: 'opt_1', label: blankI18n(), score: 0 },
            { id: 'opt_2', label: blankI18n(), score: 0 },
          ],
        },
        help_text: blankI18n(),
      };
    },

    resetForNew() {
      this.form = this.buildBlankForm();
      this.nextOptionInt = 2; // last assigned was opt_2
      this.initialSnapshot = this.serializeForm();
      this.answerCount = 0;
      this.loadError = null;
      this.serverError = null;
      this.rejectedCodes = [];
      this.touched = { code: false, title: false };
    },

    async loadExisting(id) {
      this.loadError = null;
      this.serverError = null;
      this.rejectedCodes = [];
      this.loading = true;
      try {
        const q = await apiService.fetchQuestion(id);
        if (q.question_type !== 'single_choice') {
          // The dispatcher should route here only for single_choice. If
          // we got something else, bail loudly so the misconfiguration
          // is obvious.
          this.loadError = `Question ${id} is not a single_choice question.`;
          return;
        }
        const rawOptions = Array.isArray(q.content?.options) ? q.content.options : [];
        const options = rawOptions.map(opt => ({
          id: opt.id,
          label: ensureAllLocales(opt.label),
          score: Number.isInteger(opt.score) ? opt.score : 0,
        }));
        this.form = {
          code: q.code,
          section_id: q.section_id,
          status: q.status,
          content: {
            title: ensureAllLocales(q.content?.title),
            subtitle: ensureAllLocales(q.content?.subtitle),
            prompt: ensureAllLocales(q.content?.prompt),
            tooltip_label: ensureAllLocales(q.content?.tooltip_label),
            tooltip_body: ensureAllLocales(q.content?.tooltip_body),
            options,
          },
          help_text: ensureAllLocales(q.help_text),
        };
        // Track the max opt_N integer ever seen so new options never
        // collide with old ones, even after deletes.
        this.nextOptionInt = options.reduce((m, opt) => {
          const match = OPTION_ID_RE.exec(opt.id || '');
          if (!match) return m;
          const n = parseInt(opt.id.slice(4), 10);
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        this.answerCount = q.answer_count || 0;
        this.initialSnapshot = this.serializeForm();
        this.touched = { code: false, title: false };
      } catch (e) {
        this.initialSnapshot = null;
        if (e?.response?.status === 404) {
          this.loadError = `No question found with id ${id}.`;
        } else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string'
            ? detail
            : 'Failed to load question from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    serializeForm() {
      const f = this.form;
      const options = (f.content.options || []).map(opt => {
        const label = trimI18n(opt.label);
        return {
          id: opt.id,
          label,
          score: Number.isInteger(opt.score) ? opt.score : 0,
        };
      });
      return {
        code: (f.code || '').trim(),
        section_id: f.section_id ?? null,
        status: f.status,
        content: {
          title: trimI18n(f.content.title),
          subtitle: trimI18n(f.content.subtitle),
          prompt: trimI18n(f.content.prompt),
          tooltip_label: trimI18n(f.content.tooltip_label),
          tooltip_body: trimI18n(f.content.tooltip_body),
          options,
        },
        help_text: trimI18n(f.help_text),
      };
    },

    onCodeInput(e) {
      this.form.code = (e.target.value || '').toUpperCase();
    },

    setOptionLabel(opt, locale, value) {
      // Directly mutate the reactive option's label[locale]. v-model can't
      // bind cleanly when the row template renders the active-locale value
      // dynamically, so we wire input → assignment by hand.
      opt.label[locale] = value;
    },

    addOption() {
      if (this.form.content.options.length >= 8) return;
      this.nextOptionInt += 1;
      this.form.content.options.push({
        id: `opt_${this.nextOptionInt}`,
        label: blankI18n(),
        score: 0,
      });
    },

    deleteOption(idx) {
      if (this.form.content.options.length <= 2) return;
      // Ids are not reused — nextOptionInt is not decremented here, so
      // future adds skip past any gap left by this deletion.
      this.form.content.options.splice(idx, 1);
    },

    isLocaleFilled(loc) {
      const c = this.form.content;
      const i18nFields = [c.title, c.subtitle, c.prompt, c.tooltip_label, c.tooltip_body, this.form.help_text];
      for (const f of i18nFields) {
        if (f && typeof f[loc] === 'string' && f[loc].trim()) return true;
      }
      // Options also count: a French label on any option is FR content.
      for (const opt of (c.options || [])) {
        const v = opt.label && opt.label[loc];
        if (typeof v === 'string' && v.trim()) return true;
      }
      return false;
    },

    sectionOptionLabel(section) {
      const name = localizeI18n(section.name, 'en');
      const collisions = this.sections.filter(
        s => localizeI18n(s.name, 'en') === name,
      ).length;
      if (collisions > 1 && section.questionnaire_name) {
        return `${name} (${section.questionnaire_name})`;
      }
      return name || section.slug || `Section ${section.id}`;
    },

    async fetchSections() {
      try {
        const sections = await apiService.fetchSections();
        questionsState.sections.splice(
          0,
          questionsState.sections.length,
          ...sections,
        );
      } catch (e) {
        console.warn('Could not load sections:', e);
      }
    },

    onSectionPicked(section) {
      // The popup emits the full section row. We only need its id for
      // form binding. Update the local sections cache too, so the
      // sectionPickLabel computed has the row available (the user may
      // have just created the section inside the popup).
      this.form.section_id = section.id;
      const idx = questionsState.sections.findIndex(s => s.id === section.id);
      if (idx === -1) {
        questionsState.sections.push(section);
      } else {
        questionsState.sections.splice(idx, 1, section);
      }
      this.showSectionPicker = false;
    },

    async onSave() {
      this.touched.code = true;
      this.touched.title = true;
      if (!this.isValid || this.saving) return;

      this.serverError = null;
      this.saving = true;
      const payload = this.serializeForm();

      try {
        if (this.isEdit) {
          const updated = await apiService.updateQuestion(this.questionId, {
            code: payload.code,
            section_id: payload.section_id,
            status: payload.status,
            content: payload.content,
            help_text: payload.help_text,
          });
          this._upsertCached(updated);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', 'Question saved.');
          this.$router.push('/admin/questions');
        } else {
          const created = await apiService.createQuestion({
            code: payload.code,
            section_id: payload.section_id,
            question_type: 'single_choice',
            content: payload.content,
            help_text: payload.help_text,
            status: payload.status,
          });
          this._upsertCached(created);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', `Created question ${created.code}.`);
          this.$router.push('/admin/questions');
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          const code = (this.form.code || '').trim();
          if (code && !this.rejectedCodes.includes(code)) {
            this.rejectedCodes.push(code);
          }
          this.serverError = typeof detail === 'string'
            ? detail
            : 'A question with that code already exists.';
        } else if (status === 422) {
          this.serverError = Array.isArray(detail)
            ? detail.map(d => d.msg).join(' ')
            : (typeof detail === 'string' ? detail : 'Validation failed.');
        } else if (status === 404) {
          this.serverError = 'The question no longer exists. It may have been deleted.';
        } else {
          this.serverError = typeof detail === 'string'
            ? detail
            : 'Could not save question. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    _upsertCached(item) {
      if (!item || item.id == null) return;
      const items = questionsState.items;
      const idx = items.findIndex(q => q.id === item.id);
      if (idx === -1) {
        items.push(item);
      } else {
        items.splice(idx, 1, item);
      }
    },

    onCancel() {
      if (this.isDirty) {
        this.cancelPending = true;
        this.showLeaveConfirm = true;
        return;
      }
      this.$router.push('/admin/questions');
    },

    onCancelLeave() {
      this.showLeaveConfirm = false;
      if (this.pendingNext) {
        this.pendingNext(false);
        this.pendingNext = null;
      }
      this.cancelPending = false;
    },

    onConfirmLeave() {
      this.showLeaveConfirm = false;
      if (this.cancelPending) {
        this.cancelPending = false;
        this.initialSnapshot = this.serializeForm();
        this.$router.push('/admin/questions');
        return;
      }
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        this.initialSnapshot = this.serializeForm();
        next();
      }
    },

    showToast(kind, text) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.cancelPending) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.single-choice-editor-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn: #E8A33D;
  --color-warn-soft: rgba(232, 163, 61, 0.18);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.single-choice-editor-wrap *,
.single-choice-editor-wrap *::before,
.single-choice-editor-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.breadcrumb-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.breadcrumb-link:hover {
  border-bottom-color: var(--color-text-secondary);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 4px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.server-error {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.answer-warning {
  font-size: 13px;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.5rem;
  line-height: 1.5;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.warning-icon {
  font-size: 14px;
  line-height: 1.4;
}

.load-error {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-secondary);
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 14px;
}

.back-link {
  color: var(--color-accent);
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

.editor-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 32px;
  align-items: start;
}

@media (max-width: 900px) {
  .editor-grid {
    grid-template-columns: 1fr;
  }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.preview-column {
  position: sticky;
  top: 1.5rem;
}

.preview-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  font-weight: 600;
  margin-bottom: 8px;
}

.preview-frame {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
}

.preview-note {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  font-style: italic;
  line-height: 1.4;
}

.fieldset {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem 1.5rem;
  margin: 0;
  background: var(--color-bg-card);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.fieldset-legend {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  padding: 0 6px;
  margin-left: -6px;
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
}

.option-count {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  text-transform: none;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.lang-tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-accent);
  background: var(--color-accent-soft);
  padding: 1px 6px;
  border-radius: 3px;
}

.optional {
  color: var(--color-text-tertiary);
  font-weight: 400;
  font-size: 12px;
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.4;
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.02em;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-input.has-error:focus {
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.25);
}

.field-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: var(--font-sans);
}

.field-select {
  appearance: auto;
}

/* Section picker button (replaces the legacy <select>). Shows the
   current section's display name on the left and a "Pick / Change"
   verb on the right. Clicking opens SectionPickerPopup. */
.section-pick-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.section-pick-btn:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.section-pick-btn.is-unassigned .section-pick-label {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.section-pick-action {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.section-clear-btn {
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
}

.section-clear-btn:hover {
  color: var(--color-text-secondary);
}

.field-hint {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.field-hint.dim {
  font-style: italic;
  color: var(--color-warn-dark);
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

.fieldset-error {
  margin-top: -4px;
  margin-bottom: 4px;
}

.radio-row {
  display: flex;
  gap: 18px;
  padding: 4px 0;
}

.radio-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
}

.radio-option input {
  accent-color: var(--color-accent);
  cursor: pointer;
}

/* ───────── Options list ───────── */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-row {
  display: grid;
  grid-template-columns: 22px 28px 1fr 80px 28px;
  align-items: start;
  gap: 8px;
  padding: 6px 4px;
  border-radius: var(--radius-md);
  transition: background 0.15s;
}

.option-row:hover {
  background: var(--color-bg-soft);
}

.drag-handle {
  font-size: 14px;
  color: var(--color-text-tertiary);
  cursor: grab;
  user-select: none;
  padding-top: 12px;
  text-align: center;
  letter-spacing: -2px;
}

.drag-handle:active {
  cursor: grabbing;
}

.option-number {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  padding-top: 12px;
  text-align: center;
}

.option-label-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.option-label-input {
  font-size: 13px;
  padding: 8px 10px;
}

.option-label-error {
  font-size: 11px;
}

.option-score-cell {
  display: flex;
  flex-direction: column;
}

.number-input {
  font-family: var(--font-mono);
  font-size: 13px;
  text-align: center;
  padding: 8px 6px;
}

.option-score-input {
  width: 100%;
}

.option-delete {
  width: 28px;
  height: 32px;
  margin-top: 2px;
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.option-delete:hover:not(:disabled) {
  background: var(--color-bad-soft);
  color: var(--color-bad);
  border-color: var(--color-bad-soft);
}

.option-delete:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.score-hint {
  margin-top: -2px;
}

.add-option-btn {
  align-self: flex-start;
  margin-top: 4px;
}

/* ───────── Modal + toast ───────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  max-width: min(90vw, 520px);
  z-index: 3000;
}

.toast-success {
  background: #2E7D32;
}

.toast-error {
  background: var(--color-bad-dark);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
